import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import DashboardLayout from '../../components/DashboardLayout';
import AdminOverview from './views/AdminOverview';
import AdminUsers from './views/AdminUsers';
import AdminBarbers from './views/AdminBarbers';
import AdminBookings from './views/AdminBookings';
import AdminPayments from './views/AdminPayments';
import AdminSettings from './views/AdminSettings';
import AdminEmailTemplates from './views/AdminEmailTemplates';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const navigation = [
  { name: 'Dashboard', icon: 'Grid', path: '/admin/dashboard' },
  { name: 'Users', icon: 'Users', path: '/admin/users' },
  { name: 'Barbers', icon: 'Scissors', path: '/admin/barbers' },
  { name: 'Bookings', icon: 'Calendar', path: '/admin/bookings' },
  { name: 'Payments', icon: 'CreditCard', path: '/admin/payments' },
  { name: 'Email Templates', icon: 'Mail', path: '/admin/emails' },
  { name: 'Settings', icon: 'Settings', path: '/admin/settings' },
];

export default function AdminDashboard() {
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [checking, setChecking] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [updating, setUpdating] = useState(false);
  const { toast, showToast, hideToast } = useToast();

  useEffect(() => {
    const checkProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase
          .from('profiles_1740423033529')
          .select('requires_password_change')
          .eq('id', user.id)
          .single();
          
        if (data?.requires_password_change) {
          setRequiresPasswordChange(true);
        }
      }
      setChecking(false);
    };
    checkProfile();
  }, []);

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      showToast("Passwords do not match", "error");
      return;
    }
    if (newPassword.length < 6) {
      showToast("Password must be at least 6 characters", "error");
      return;
    }
    
    setUpdating(true);
    try {
      // 1. Update Auth Password
      const { error: authError } = await supabase.auth.updateUser({ password: newPassword });
      if (authError) throw authError;

      // 2. Clear the flag in the database
      const { data: { user } } = await supabase.auth.getUser();
      const { error: profileError } = await supabase
        .from('profiles_1740423033529')
        .update({ requires_password_change: false })
        .eq('id', user.id);
        
      if (profileError) throw profileError;

      setRequiresPasswordChange(false);
      showToast("Password updated successfully! Welcome.", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setUpdating(false);
    }
  };

  if (checking) return null;

  if (requiresPasswordChange) {
    return (
      <div className="min-h-screen bg-brand-dark flex flex-col items-center justify-center p-6 relative">
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        <div className="bg-white p-10 rounded-[2.5rem] shadow-2xl max-w-md w-full animate-in zoom-in-95">
          <div className="w-16 h-16 bg-brand-primary/10 text-brand-primary rounded-2xl flex items-center justify-center mx-auto mb-6">
            <SafeIcon name="Lock" className="text-3xl" />
          </div>
          <h1 className="text-2xl font-black text-brand-dark text-center mb-2">Welcome, Admin!</h1>
          <p className="text-gray-500 text-sm text-center mb-8 font-medium">
            For security reasons, you must set a new permanent password before accessing the admin panel.
          </p>
          <form onSubmit={handlePasswordUpdate} className="space-y-5">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">New Password</label>
              <input
                type="password"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                placeholder="Min 6 characters"
              />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Confirm Password</label>
              <input
                type="password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-5 py-4 border border-gray-200 rounded-xl bg-gray-50 outline-none focus:border-brand-primary font-bold text-brand-dark"
                placeholder="Confirm new password"
              />
            </div>
            <Button type="submit" className="w-full py-5 text-lg font-black rounded-xl shadow-xl mt-4" disabled={updating}>
              {updating ? 'Saving...' : 'Set Password & Continue'}
            </Button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout role="admin" navigation={navigation}>
      <Routes>
        <Route path="dashboard" element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="barbers" element={<AdminBarbers />} />
        <Route path="bookings" element={<AdminBookings />} />
        <Route path="payments" element={<AdminPayments />} />
        <Route path="emails" element={<AdminEmailTemplates />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="*" element={<Navigate to="dashboard" replace />} />
      </Routes>
    </DashboardLayout>
  );
}